import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'environments/environment';

/**
 * Self-service affiliate portal HTTP wrapper.
 *
 * Mirrors the backend AffiliatePortalController. Every endpoint resolves the
 * authenticated affiliate from the JWT — never from path/body params.
 */

export interface AffiliatePortalProfile {
    idAffiliate: number;
    referralCode: string;
    firstName: string;
    lastName: string;
    email: string;
    status: string;

    phone: string | null;
    country: string | null;
    companyName: string | null;
    vatNumber: string | null;

    /**
     * Payout block — READ-ONLY on the portal. Set exclusively by the admin
     * back-office. Only the destination matching {@link payoutMethod} is non-null.
     */
    payoutMethod: string | null;
    payoutPaypalEmail: string | null;
    payoutIban: string | null;
    payoutInstapayPhone: string | null;
    payoutInstapayPhoneCountryIso: string | null;

    commissionRate: number;
    commissionDurationMonths: number;
    holdPeriodDays: number;

    approvedAt: string | null;
}

/** Editable fields only — payout block is admin-managed. */
export interface AffiliatePortalUpdatePayload {
    phone?: string;
    country?: string;
    companyName?: string;
    vatNumber?: string;
}

export interface AffiliatePortalPasswordChangePayload {
    currentPassword: string;
    newPassword: string;
}

export interface CommissionBreakdown {
    status: string;
    currency: string;
    amount: number;
}

export interface CurrencyAmount {
    currency: string;
    amount: number;
}

export interface AffiliatePortalStats {
    totalReferredPartners: number;
    activeReferredPartners: number;
    accruedByStatus: CommissionBreakdown[];
    thisMonthAccrued: CurrencyAmount[];
}

export interface AnonymizedReferredPartner {
    country: string | null;
    city: string | null;
    signupDate: string;
    referralCodeUsed: string | null;
    currentPlanCode: string | null;
    /** Plan price in the natural currency unit (e.g. 9 for $9/month, 90 for $90/year). */
    currentPlanPrice: number | null;
    yearlyBilling: boolean | null;
    currencyCode: string | null;
    /** Whole months remaining; null when no paid invoice yet. */
    commissionWindowMonthsRemaining: number | null;
}

export interface AffiliatePortalCommission {
    idCommission: number;
    /** Anonymized label like "Partner A". Stable per-page only. */
    partnerRef: string | null;
    invoiceDate: string;
    subscriptionAmount: number;
    commissionAmount: number;
    commissionRate: number;
    currency: string;
    status: string;
    cancellationReason: string | null;
    approvedAt: string | null;
    /** PENDING-only: when the row will become payable. */
    willApproveAt: string | null;
    payoutId: number | null;
    createdAt: string;
}

export interface AffiliatePortalPayout {
    idPayout: number;
    periodStart: string;
    periodEnd: string;
    totalAmount: number;
    currency: string;
    method: string;
    status: string;

    /** Recipient snapshot frozen at batch generation. Only the field matching method is non-null. */
    recipientPaypalEmail: string | null;
    recipientIban: string | null;
    recipientInstapayPhone: string | null;
    recipientInstapayPhoneCountryIso: string | null;

    processedAt: string | null;
    externalReference: string | null;
}

/** Spring Data Page<T> wire format. */
export interface PagedResponse<T> {
    content: T[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
}

@Injectable({ providedIn: 'root' })
export class AffiliatePortalService {

    private readonly baseUrl = '/affiliate-portal';

    constructor(private _http: HttpClient) {}

    // ----- Profile -----

    getProfile(): Observable<AffiliatePortalProfile> {
        return this._http.get<AffiliatePortalProfile>(`${environment.api}${this.baseUrl}/me`);
    }

    updateProfile(payload: AffiliatePortalUpdatePayload): Observable<AffiliatePortalProfile> {
        return this._http.put<AffiliatePortalProfile>(`${environment.api}${this.baseUrl}/me`, payload);
    }

    changePassword(payload: AffiliatePortalPasswordChangePayload): Observable<void> {
        return this._http.put<void>(`${environment.api}${this.baseUrl}/me/password`, payload);
    }

    updateLanguage(languageRefCode: string): Observable<void> {
        return this._http.put<void>(`${environment.api}${this.baseUrl}/me/language`, { languageRefCode });
    }

    // ----- Stats -----

    getStats(): Observable<AffiliatePortalStats> {
        return this._http.get<AffiliatePortalStats>(`${environment.api}${this.baseUrl}/me/stats`);
    }

    // ----- Referred partners (anonymized) -----

    getReferredPartners(page: number = 0, size: number = 20): Observable<PagedResponse<AnonymizedReferredPartner>> {
        const params = new HttpParams().set('page', page).set('size', size);
        return this._http.get<PagedResponse<AnonymizedReferredPartner>>(
            `${environment.api}${this.baseUrl}/me/referred-partners`,
            { params }
        );
    }

    // ----- Commissions -----

    getCommissions(opts: {
        page?: number;
        size?: number;
        status?: string;
        currency?: string;
        from?: string;
        to?: string;
    } = {}): Observable<PagedResponse<AffiliatePortalCommission>> {
        let params = new HttpParams()
            .set('page', opts.page ?? 0)
            .set('size', opts.size ?? 20);
        if (opts.status) { params = params.set('status', opts.status); }
        if (opts.currency) { params = params.set('currency', opts.currency); }
        if (opts.from) { params = params.set('from', opts.from); }
        if (opts.to) { params = params.set('to', opts.to); }
        return this._http.get<PagedResponse<AffiliatePortalCommission>>(
            `${environment.api}${this.baseUrl}/me/commissions`,
            { params }
        );
    }

    // ----- Payouts -----

    getPayouts(opts: { page?: number; size?: number; status?: string } = {}): Observable<PagedResponse<AffiliatePortalPayout>> {
        let params = new HttpParams()
            .set('page', opts.page ?? 0)
            .set('size', opts.size ?? 20);
        if (opts.status) { params = params.set('status', opts.status); }
        return this._http.get<PagedResponse<AffiliatePortalPayout>>(
            `${environment.api}${this.baseUrl}/me/payouts`,
            { params }
        );
    }
}
